import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST, THIS_MONTH, TODAY } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { substanceDrugTypeValidator } from 'forms/sections/substance/shared/drugTypeValidator';
import { getSafeDateTime, isSafeDateLater, setToStartOfMonth } from 'helpers/date';
import { formConfig } from 'config/forms';
import { SafeDateTime } from 'helpers/types';
import { isPVQForm } from 'helpers/form';
import { SubstanceDrugsInvolvementItemModel } from '../types';

const substanceDrugsInvolvementItemValidator: ValidateJSModel<SubstanceDrugsInvolvementItemModel> = {
  DrugType: (value, attributes, attributeName, options = {}) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return substanceDrugTypeValidator;
  },
  DrugTypeOther: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.DrugType?.value === 'Other') {
        return { presence: true, hasValue: true };
      }
    }
    return {};
  },
  FirstInvolvement: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (!options?.applicantBirthdate) { return {}; }
      const applicantBirthdate: SafeDateTime = getSafeDateTime(options?.applicantBirthdate);
      const sixteenthBirthdate: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 16 }));
      const twentyFirstBirthday: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 21 }));
      const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));
      // if 21 bday is before (or on) today applicant is over 21
      const isOverTwentyOne: boolean = twentyFirstBirthday.valid ? isSafeDateLater(getSafeDateTime(DEFAULT_LATEST), twentyFirstBirthday) : true;
      const statuteOfLimitation = isOverTwentyOne ? fiveYearsAgo : sixteenthBirthdate;
      return {
        presence: true,
        date: {
          requireDay: false,
          requireYear: true,
          latest: DEFAULT_LATEST,
          earliest: statuteOfLimitation.valid ? statuteOfLimitation.datetime : DEFAULT_EARLIEST,
          tooEarly: 'error.beforeDuration'
        },
      };
    }

    return {
      presence: true,
      date: { requireDay: false, earliest: setToStartOfMonth(options?.applicantBirthdate), latest: DEFAULT_LATEST },
    };
  },
  RecentInvolvement: (value, attributes, attributeName, options) => {
    if (!attributes?.FirstInvolvement) {
      return {};
    }

    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (!options?.applicantBirthdate || !attributes?.FirstInvolvement) { return {}; }
      const applicantBirthdate: SafeDateTime = getSafeDateTime(options?.applicantBirthdate);
      const firstUseDate: SafeDateTime = getSafeDateTime(attributes?.FirstInvolvement);
      const sixteenthBirthdate: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 16 }));
      const twentyFirstBirthday: SafeDateTime = getSafeDateTime(applicantBirthdate?.datetime?.plus({ years: 21 }));
      const fiveYearsAgo: SafeDateTime = getSafeDateTime(DEFAULT_LATEST.minus({ years: 5 }));
      // if 21 bday is before (or on) today applicant is over 21
      const isOverTwentyOne: boolean = isSafeDateLater(getSafeDateTime(DEFAULT_LATEST), twentyFirstBirthday);
      const stauteOfLimitation = isOverTwentyOne ? fiveYearsAgo : sixteenthBirthdate;
      const isFirstUseBeforeStaute = isSafeDateLater(firstUseDate, stauteOfLimitation);

      return {
        presence: true,
        date: {
          requireDay: false,
          requireYear: false,
          latest: DEFAULT_LATEST,
          earliest: isFirstUseBeforeStaute ? setToStartOfMonth(firstUseDate).datetime : setToStartOfMonth(stauteOfLimitation).datetime,
          tooEarly: isFirstUseBeforeStaute ? 'error.drugUsage.min' : 'error.beforeDuration'
        },
      };
    }

    const formTypeConfig = options?.formType && formConfig[options?.formType as keyof typeof formConfig];
    const applicantEighteenthBirthdate: SafeDateTime | undefined = options?.applicantBirthdate && getSafeDateTime(getSafeDateTime(options?.applicantBirthdate)?.datetime?.plus({ years: 18 }));
    const years: any = formTypeConfig && formTypeConfig.SUBSTANCE_DRUG_USE_YEARS;

    let beyondRequired: SafeDateTime = getSafeDateTime(THIS_MONTH);
    let useDuration = false;

    if (years && beyondRequired?.valid && typeof years === 'number') {
      if (options?.formType && options?.formType === 'SF86' && applicantEighteenthBirthdate?.valid
        && applicantEighteenthBirthdate.datetime.year < (TODAY.year - years)) {
        beyondRequired = setToStartOfMonth(applicantEighteenthBirthdate);
      } else {
        beyondRequired.datetime = beyondRequired?.datetime.minus({ years });
      }

      useDuration = isSafeDateLater(beyondRequired, getSafeDateTime(attributes?.FirstInvolvement));
    }

    return {
      presence: true,
      date: {
        requireDay: false,
        latest: DEFAULT_LATEST,
        earliest: useDuration ? setToStartOfMonth(beyondRequired).datetime : attributes?.FirstInvolvement,
        tooEarly: useDuration ? 'error.beforeDuration' : 'error.drugUsage.min',
      },
    };
  },
  HowManyTimes: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  InvolvementWhileEmployedPosition: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.InvolvementWhileEmployed?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },
  InvolvementWhileEmployedExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.InvolvementWhileEmployed?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },

  InvolvementWithClearanceExplanation: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      if (attributes?.InvolvementWithClearance?.value === 'Yes') { return { presence: true, hasValue: true }; }
    }
    return {};
  },

  NatureOfInvolvement: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return {};
    }
    return { presence: true, hasValue: true };
  },
  Reasons: { presence: true, hasValue: true },
  InvolvementWhileEmployed: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options && options?.requireInvolvementWhileEmployed === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  InvolvementWithClearance: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options && options?.requireInvolvementWithClearance === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  InvolvementInFuture: (value, attributes, attributeName, options) => {
    const isPVQ = isPVQForm(options?.formType);
    if (isPVQ) {
      return { presence: true, hasValue: { validator: hasYesOrNo } };
    }
    if (options && options?.requireInvolvementInFuture === false) { return {}; }
    return { presence: true, hasValue: { validator: hasYesOrNo } };
  },
  Explanation: (value, attributes, attributeName, options) => {
    if (
      attributes?.InvolvementInFuture
      && (
        attributes?.InvolvementInFuture.value === 'Yes'
        || (isPVQForm(options?.formType) && attributes?.InvolvementInFuture.value) === 'No')
    ) {
      return { presence: true, hasValue: true };
    }
    return {};
  },
  Comments: {},
  InvolvementInFutureComments: {},
};

export default substanceDrugsInvolvementItemValidator;
